import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Table from "@/components/ui/table";
import type { TableColumn } from "@/types/component";
import ReportDetailLayout from "../../components/ReportDetailLayout";
import ReportFilterBar, {
  type ReportFilterState,
} from "../../components/ReportFilterBar";
import ReportKPICard from "../../components/ReportKPICard";
import ReportChart from "../../components/ReportChart";
import ReportExportButtons from "../../components/ReportExportButtons";
import { reportsExtendedApi } from "../../services/reports-extended.api";
import { useReportData } from "../../hooks/useReportData";
import { apiClient } from "@/lib/apiClient";
import { Certificate, Recent, Shield } from "@/components/icons";
import styles from "./CertificateValidityReportPage.module.scss";

const PAGE_SIZE = 20;
const BASE = "/api/admin/reports";

// ---------- Types ----------
export type CertificateValidityStatus =
  | "ACTIVE"
  | "EXPIRING_SOON"
  | "EXPIRED"
  | "REVOKED"
  | "PERMANENT";

export type CertificateValidityStatusFilter =
  | "active"
  | "expiring30"
  | "expiring60"
  | "expiring90"
  | "expired"
  | "revoked";

export interface CertificateValidityItem {
  certificateID: string;
  userID: string;
  userFullName: string;
  department: string | null;
  certificateName: string;
  issuedAt: string;
  expiresAt: string | null;
  validityMonths: number | null;
  daysToExpiry: number | null;
  status: CertificateValidityStatus;
}

export interface CertificateValiditySummary {
  totalIssued: number;
  active: number;
  expiringIn30Days: number;
  expiringIn60Days: number;
  expiringIn90Days: number;
  expired: number;
  revoked: number;
}

export interface CertificateValidityResponse {
  summary: CertificateValiditySummary;
  expiryTimeline: { month: string; expiring: number }[];
  byCategory: { label: string; count: number }[];
  byMonthlyIssuance: { month: string; issued: number }[];
  items: CertificateValidityItem[];
  count: number;
}

export interface CertificateValidityParams {
  page?: number;
  limit?: number;
  search?: string;
  departmentID?: string;
  dateFrom?: string;
  dateTo?: string;
  status?: CertificateValidityStatusFilter;
}

// ---------- Local fetcher (TransformResponseInterceptor unwrap) ----------
function unwrap<T>(body: { data?: T } | T): T {
  if (body && typeof body === "object" && "data" in (body as object)) {
    return (body as { data: T }).data;
  }
  return body as T;
}

const fetchCertificateValidity = async (
  params: CertificateValidityParams,
  signal?: AbortSignal,
): Promise<CertificateValidityResponse> => {
  const { data } = await apiClient.get(`${BASE}/certificate-validity`, {
    params,
    signal,
  });
  return unwrap<CertificateValidityResponse>(data);
};

// ---------- Helpers ----------
const formatNumber = (v: number) =>
  new Intl.NumberFormat("tr-TR").format(Math.round(v ?? 0));

const formatDate = (iso: string | null | undefined) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("tr-TR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const formatMonth = (key: string) => {
  // key formatı YYYY-MM
  const [y, m] = key.split("-");
  if (!y || !m) return key;
  const d = new Date(Date.UTC(Number(y), Number(m) - 1, 1));
  return d.toLocaleDateString("tr-TR", { month: "short", year: "2-digit" });
};

const CertificateValidityReportPage: React.FC = () => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<ReportFilterState>({});
  const [page, setPage] = useState(1);

  const params: CertificateValidityParams = useMemo(
    () => ({
      page,
      limit: PAGE_SIZE,
      search: filter.search?.trim() || undefined,
      departmentID: filter.departmentID || undefined,
      dateFrom: filter.dateFrom || undefined,
      dateTo: filter.dateTo || undefined,
    }),
    [filter, page],
  );

  const { data, isLoading, error } = useReportData<
    CertificateValidityResponse,
    CertificateValidityParams
  >({
    fetcher: (p, signal) => fetchCertificateValidity(p, signal),
    params,
  });

  const summary = data?.summary;
  const expiryTimeline = data?.expiryTimeline ?? [];
  const byCategory = data?.byCategory ?? [];
  const byMonthlyIssuance = data?.byMonthlyIssuance ?? [];
  const items = data?.items ?? [];
  const count = data?.count ?? 0;

  const statusBadge = (status: CertificateValidityStatus) => {
    const map: Record<
      CertificateValidityStatus,
      { label: string; cls: string }
    > = {
      ACTIVE: {
        label: t("reports.certificateValidity.status.active"),
        cls: styles.badgeActive,
      },
      EXPIRING_SOON: {
        label: t("reports.certificateValidity.status.expiringSoon"),
        cls: styles.badgeWarning,
      },
      EXPIRED: {
        label: t("reports.certificateValidity.status.expired"),
        cls: styles.badgeDanger,
      },
      REVOKED: {
        label: t("reports.certificateValidity.status.revoked"),
        cls: styles.badgeMuted,
      },
      PERMANENT: {
        label: t("reports.certificateValidity.status.permanent"),
        cls: styles.badgePermanent,
      },
    };
    const cfg = map[status];
    return <span className={`${styles.badge} ${cfg.cls}`}>{cfg.label}</span>;
  };

  const renderRemainingDays = (row: CertificateValidityItem) => {
    if (row.daysToExpiry === null) {
      return (
        <span className={styles.numCellMuted}>
          {t("reports.certificateValidity.daysToExpiry.permanent")}
        </span>
      );
    }
    if (row.daysToExpiry < 0) {
      return (
        <span className={styles.numCellDanger}>
          {t("reports.certificateValidity.daysToExpiry.expired", {
            count: Math.abs(row.daysToExpiry),
          })}
        </span>
      );
    }
    if (row.daysToExpiry <= 30) {
      return (
        <span className={styles.numCellWarning}>
          {t("reports.certificateValidity.daysToExpiry.days", {
            count: row.daysToExpiry,
          })}
        </span>
      );
    }
    return (
      <span className={styles.numCell}>
        {t("reports.certificateValidity.daysToExpiry.days", {
          count: row.daysToExpiry,
        })}
      </span>
    );
  };

  const columns: TableColumn<CertificateValidityItem>[] = [
    {
      header: t("reports.certificateValidity.headers.user"),
      accessor: (row) => (
        <span className={styles.titleCell}>{row.userFullName || "—"}</span>
      ),
    },
    {
      header: t("reports.certificateValidity.headers.certificate"),
      accessor: (row) => (
        <span className={styles.titleCell}>{row.certificateName || "—"}</span>
      ),
    },
    {
      header: t("reports.certificateValidity.headers.department"),
      accessor: (row) => (
        <span className={styles.numCell}>{row.department || "—"}</span>
      ),
    },
    {
      header: t("reports.certificateValidity.headers.issuedAt"),
      accessor: (row) => (
        <span className={styles.numCell}>{formatDate(row.issuedAt)}</span>
      ),
    },
    {
      header: t("reports.certificateValidity.headers.expiresAt"),
      accessor: (row) => (
        <span className={styles.numCell}>
          {row.expiresAt ? formatDate(row.expiresAt) : "—"}
        </span>
      ),
    },
    {
      header: t("reports.certificateValidity.headers.daysRemaining"),
      accessor: renderRemainingDays,
    },
    {
      header: t("reports.certificateValidity.headers.status"),
      accessor: (row) => statusBadge(row.status),
    },
  ];

  return (
    <ReportDetailLayout
      title={t("reports.certificateValidity.title")}
      categoryLabel={t("reports.certificateValidity.categoryLabel")}
      description={t("reports.certificateValidity.description")}
      exportSlot={
        <ReportExportButtons
          reportType="CERTIFICATE_VALIDITY"
          filters={params as unknown as Record<string, unknown>}
          filenameBase="sertifika-gecerlilik"
          exportFn={reportsExtendedApi.exportReport}
          disabled={isLoading}
        />
      }
      filterSlot={
        <ReportFilterBar
          value={filter}
          onChange={(next) => {
            setFilter(next);
            setPage(1);
          }}
          showDateRange
        />
      }
    >
      <div className={styles.kpiGrid}>
        <ReportKPICard
          title={t("reports.certificateValidity.kpi.active")}
          value={summary ? formatNumber(summary.active) : "—"}
          icon={<Certificate width={18} height={18} />}
          accent="success"
        />
        <ReportKPICard
          title={t("reports.certificateValidity.kpi.expiringIn30")}
          value={summary ? formatNumber(summary.expiringIn30Days) : "—"}
          icon={<Recent width={18} height={18} />}
          accent="warning"
          footnote={t("reports.certificateValidity.kpi.expiringIn30Note")}
        />
        <ReportKPICard
          title={t("reports.certificateValidity.kpi.expired")}
          value={summary ? formatNumber(summary.expired) : "—"}
          icon={<Shield width={18} height={18} />}
          accent="danger"
        />
        <ReportKPICard
          title={t("reports.certificateValidity.kpi.revoked")}
          value={summary ? formatNumber(summary.revoked) : "—"}
          icon={<Shield width={18} height={18} />}
        />
      </div>

      <div className={styles.chartGrid}>
        <ReportChart
          title={t("reports.certificateValidity.charts.expiryTimeline")}
          subtitle={t("reports.certificateValidity.charts.expiryTimelineSubtitle")}
          type="area"
          isLoading={isLoading}
          isEmpty={expiryTimeline.length === 0}
          height={280}
          series={[
            {
              name: t("reports.certificateValidity.charts.expiringSeries"),
              data: expiryTimeline.map((b) => b.expiring),
            },
          ]}
          options={{
            xaxis: { categories: expiryTimeline.map((b) => formatMonth(b.month)) },
            colors: ["#f59e0b"],
            stroke: { curve: "smooth", width: 2 },
            fill: {
              type: "gradient",
              gradient: { opacityFrom: 0.45, opacityTo: 0.05 },
            },
            dataLabels: { enabled: false },
            legend: { show: false },
          }}
        />

        <ReportChart
          title={t("reports.certificateValidity.charts.monthlyIssuance")}
          subtitle={t("reports.certificateValidity.charts.monthlyIssuanceSubtitle")}
          type="bar"
          isLoading={isLoading}
          isEmpty={byMonthlyIssuance.length === 0}
          height={280}
          series={[
            {
              name: t("reports.certificateValidity.charts.issuedSeries"),
              data: byMonthlyIssuance.map((b) => b.issued),
            },
          ]}
          options={{
            xaxis: {
              categories: byMonthlyIssuance.map((b) => formatMonth(b.month)),
            },
            colors: ["#4f46e5"],
            plotOptions: {
              bar: { borderRadius: 6, columnWidth: "55%" },
            },
            legend: { show: false },
            dataLabels: { enabled: false },
          }}
        />

        <ReportChart
          title={t("reports.certificateValidity.charts.byCategory")}
          subtitle={t("reports.certificateValidity.charts.byCategorySubtitle")}
          type="donut"
          isLoading={isLoading}
          isEmpty={byCategory.length === 0}
          height={280}
          series={byCategory.map((c) => c.count)}
          options={{
            labels: byCategory.map((c) => c.label),
            legend: { position: "bottom" },
            plotOptions: {
              pie: {
                donut: { size: "65%" },
              },
            },
            dataLabels: { enabled: false },
          }}
        />
      </div>

      <Table
        columns={columns}
        data={items}
        isLoading={isLoading}
        contentBasedHeight
        getRowId={(row) => row.certificateID}
        totalRecords={count}
        currentPage={page}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        emptyMessage={
          error
            ? t("reports.certificateValidity.noData")
            : t("reports.certificateValidity.noItems")
        }
      />
    </ReportDetailLayout>
  );
};

export default CertificateValidityReportPage;
